// Library Methods
import {
  CSSProperties,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { keyframes, useTheme } from "@mui/material/styles";
import { PauseIcon, PlayIcon } from "src/assets/icons";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import { GUIDE_NAMESPACE, Rich } from "../GuideText/GuideText";

// Utils
import {
  GUIDE_INNER_ATTRIBUTE,
  GUIDE_TARGET_ATTRIBUTE,
  WalkthroughStep,
  WalkthroughView,
  resolveWalkthroughView,
} from "./walkthroughState";
import { GuideArrow, computeGuideArrow } from "./walkthroughArrow";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirmTeal,
  getEmrPageBackground,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

export const WALKTHROUGH_AUTOPLAY_MS = 3800;

const draw = keyframes`to { stroke-dashoffset: 0; }`;
const appear = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const bob = keyframes`50% { transform: translate(var(--guide-nudge-x), var(--guide-nudge-y)); }`;
const pulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(53, 188, 253, 0.55); } 100% { box-shadow: 0 0 0 12px rgba(53, 188, 253, 0); }`;

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

const findTarget = (stage: HTMLElement, id: string | undefined) => {
  if (!id) return null;

  const visible = Array.from(
    stage.querySelectorAll<HTMLElement>(`[${GUIDE_TARGET_ATTRIBUTE}="${id}"]`)
  ).find((element) => element.getClientRects().length > 0);

  if (!visible) return null;

  const inner = visible.getAttribute(GUIDE_INNER_ATTRIBUTE);

  return inner ? visible.querySelector<HTMLElement>(inner) ?? visible : visible;
};

type WalkthroughType = {
  id: string;
  steps: Array<WalkthroughStep>;
  children: (view: WalkthroughView) => ReactNode;
};

const Walkthrough = ({ id, steps, children }: WalkthroughType) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { surface, mutedText, dividerBorder } = getSurfaceTokens(theme);
  const isDark = theme.palette.mode === "dark";
  const arrowColor = isDark ? "#35BCFD" : "#3179FE";
  const haloColor = isDark ? "#12141C" : "#FFFFFF";

  const stageRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [arrow, setArrow] = useState<GuideArrow | null>(null);

  const view = useMemo(
    () => resolveWalkthroughView(steps, index),
    [steps, index]
  );
  const isLast = index === steps.length - 1;

  const goTo = useCallback(
    (next: number) => setIndex((next + steps.length) % steps.length),
    [steps.length]
  );

  const stopAndGoTo = (next: number) => {
    setIsPlaying(false);
    goTo(next);
  };

  const measure = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const element = findTarget(stage, steps[index]?.target);
    if (!element) {
      setArrow(null);
      return;
    }

    const stageBox = stage.getBoundingClientRect();
    const box = element.getBoundingClientRect();

    setArrow(
      computeGuideArrow(stageBox.width, stageBox.height, {
        left: box.left - stageBox.left,
        top: box.top - stageBox.top,
        width: box.width,
        height: box.height,
      })
    );
  }, [index, steps]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(stage);

    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    stageRef.current?.setAttribute("inert", "");
  }, []);

  useEffect(() => {
    if (!isPlaying) return;

    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % steps.length),
      WALKTHROUGH_AUTOPLAY_MS
    );

    return () => window.clearInterval(timer);
  }, [isPlaying, steps.length]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowRight") stopAndGoTo(index + 1);
    if (event.key === "ArrowLeft") stopAndGoTo(index - 1);
  };

  return (
    <Box
      component="figure"
      data-testid={`walkthrough-${id}`}
      onKeyDown={handleKeyDown}
      sx={{
        margin: 0,
        border: dividerBorder,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: surface,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          padding: "10px 14px",
          borderBottom: dividerBorder,
          fontSize: "12px",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontWeight: 700,
          color: mutedText,
        }}
      >
        <Box display="flex" alignItems="center" gap="6px">
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: ACCENT_TEAL,
            }}
          />
          {t("Walkthrough")}
        </Box>
        <span>{`${steps.length} ${t("steps")}`}</span>
      </Box>

      <Box
        ref={stageRef}
        aria-hidden="true"
        data-testid={`walkthrough-${id}-stage`}
        sx={{
          position: "relative",
          overflow: "hidden",
          padding: { xs: "10px", sm: "18px" },
          background: getEmrPageBackground(theme),
        }}
      >
        {children(view)}

        {arrow && (
          <>
            <Box
              key={`ring-${index}`}
              sx={{
                position: "absolute",
                zIndex: 29,
                pointerEvents: "none",
                left: arrow.ring.left,
                top: arrow.ring.top,
                width: arrow.ring.width,
                height: arrow.ring.height,
                border: `3px solid ${arrowColor}`,
                borderRadius: "9px",
                animation: `${pulse} 1.6s ease-out infinite`,
                [REDUCED_MOTION]: { animation: "none" },
              }}
            />
            <Box
              component="svg"
              key={`arrow-${index}`}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                overflow: "visible",
                zIndex: 30,
                "& .guide-bob": {
                  animation: `${bob} 1.4s ease-in-out 0.7s infinite`,
                },
                "& .guide-draw": {
                  strokeDasharray: 1,
                  strokeDashoffset: 1,
                  animation: `${draw} 0.55s ease-out forwards`,
                },
                "& .guide-head": {
                  opacity: 0,
                  animation: `${appear} 0.2s ease-out 0.45s forwards`,
                },
                [REDUCED_MOTION]: {
                  "& .guide-bob, & .guide-draw, & .guide-head": {
                    animation: "none",
                    strokeDashoffset: 0,
                    opacity: 1,
                  },
                },
              }}
            >
              <g
                className="guide-bob"
                style={
                  {
                    "--guide-nudge-x": `${arrow.nudgeX.toFixed(1)}px`,
                    "--guide-nudge-y": `${arrow.nudgeY.toFixed(1)}px`,
                  } as CSSProperties
                }
              >
                <path
                  className="guide-draw"
                  d={arrow.path}
                  pathLength={1}
                  fill="none"
                  stroke={haloColor}
                  strokeWidth={9}
                  strokeLinecap="round"
                  opacity={0.9}
                />
                <path
                  className="guide-draw"
                  d={arrow.path}
                  pathLength={1}
                  fill="none"
                  stroke={arrowColor}
                  strokeWidth={4}
                  strokeLinecap="round"
                />
                <path
                  className="guide-head"
                  d={arrow.head}
                  fill={arrowColor}
                  stroke={haloColor}
                  strokeWidth={2}
                  strokeLinejoin="round"
                />
              </g>
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "minmax(0, 1fr) auto",
          },
          gap: "12px",
          alignItems: "center",
          padding: "12px 14px",
          borderTop: dividerBorder,
        }}
      >
        <Box
          component="figcaption"
          aria-live="polite"
          display="flex"
          gap="10px"
          alignItems="flex-start"
          minHeight="46px"
        >
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              backgroundColor: ACCENT_TEAL,
              color: "#FFF",
              fontWeight: 700,
              fontSize: "12px",
              borderRadius: "999px",
              padding: "3px 9px",
              marginTop: "1px",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {`${index + 1} / ${steps.length}`}
          </Box>
          <Typography
            component="span"
            fontSize="14px"
            lineHeight={1.5}
            data-testid={`walkthrough-${id}-caption`}
          >
            <Rich text={steps[index].text} />
          </Typography>
        </Box>
        <Box display="flex" gap="6px" justifyContent="flex-end">
          <Button
            sx={ModalCustomCancel}
            disabled={index === 0}
            onClick={() => stopAndGoTo(index - 1)}
          >
            {t("Back")}
          </Button>
          <Button
            sx={ModalCustomConfirmTeal}
            onClick={() => stopAndGoTo(index + 1)}
          >
            {isLast ? t("Start over") : t("Next")}
          </Button>
          <IconButton
            aria-label={
              isPlaying ? t("Pause") : t("Play the steps automatically")
            }
            onClick={() => {
              if (!isPlaying && isLast) goTo(0);
              setIsPlaying(!isPlaying);
            }}
            sx={{ border: dividerBorder, borderRadius: "6px" }}
          >
            {isPlaying ? (
              <PauseIcon width="18px" height="18px" />
            ) : (
              <PlayIcon width="18px" height="18px" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box display="flex" gap="5px" flexWrap="wrap" padding="0 14px 12px">
        {steps.map((step, stepIndex) => (
          <Box
            key={`${step.text}-${stepIndex}`}
            component="button"
            type="button"
            aria-label={t("Go to step {{number}}", { number: stepIndex + 1 })}
            aria-current={stepIndex === index ? "step" : undefined}
            onClick={() => stopAndGoTo(stepIndex)}
            sx={{
              width: "22px",
              height: "6px",
              borderRadius: "3px",
              border: 0,
              padding: 0,
              cursor: "pointer",
              backgroundColor:
                stepIndex === index
                  ? ACCENT_TEAL
                  : stepIndex < index
                    ? "rgba(27, 143, 118, 0.45)"
                    : "#5B6D7A50",
              "&:focus-visible": { outline: "3px solid #35BCFD" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Walkthrough;
